import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from 'appHooks'
import type { ComposeRequest, ComposeSize } from './shared/compose.types'

type ComposeState = {
  request: ComposeRequest | null
  /** Opened while another compose was open; it replaces that one once it closes. */
  pending: ComposeRequest | null
  /** Something else (a client switch) needs the compose closed; nothing opens after it. */
  closeRequested: boolean
  size: ComposeSize
}

const initialState: ComposeState = {
  request: null,
  pending: null,
  closeRequested: false,
  size: 'full',
}

const isSameRequest = (a: ComposeRequest, b: ComposeRequest) =>
  JSON.stringify(a) === JSON.stringify(b)

const composeEmailSlice = createSlice({
  name: 'composeEmail',
  initialState,
  reducers: {
    // One compose at a time. A minimized one leaves the page usable, so a
    // second open waits until the first is closed, after asking when dirty.
    openCompose: (state, action: PayloadAction<ComposeRequest>) => {
      if (!state.request || isSameRequest(state.request, action.payload)) {
        state.request = action.payload
        state.size = 'full'
      } else {
        state.pending = action.payload
        state.closeRequested = false
      }
    },
    // Latest intent wins, so this and a pending open never coexist.
    requestComposeClose: (state) => {
      if (!state.request) return
      state.closeRequested = true
      state.pending = null
    },
    closeCompose: (state) => {
      state.request = state.pending
      state.pending = null
      state.closeRequested = false
      state.size = 'full'
    },
    cancelPendingCompose: (state) => {
      state.pending = null
      state.closeRequested = false
    },
    resizeCompose: (state, action: PayloadAction<ComposeSize>) => {
      state.size = action.payload
    },
  },
})

export const {
  openCompose,
  closeCompose,
  cancelPendingCompose,
  requestComposeClose,
  resizeCompose,
} = composeEmailSlice.actions

export const selectComposeRequest = (state: RootState) =>
  state.composeEmail.request

/** The open compose has to close: another one waits, or a close was requested. */
export const selectIsComposeReplaced = (state: RootState) =>
  !!state.composeEmail.pending || state.composeEmail.closeRequested

export const selectIsComposeCloseRequested = (state: RootState) =>
  state.composeEmail.closeRequested

export const selectComposeSize = (state: RootState) => state.composeEmail.size

export const selectIsComposeCollapsed = (state: RootState) =>
  !!state.composeEmail.request && state.composeEmail.size === 'collapsed'

export default composeEmailSlice
